import React from 'react'
import { geometricToVisualRect, visualInsetRatiosForShape } from '@/lib/shapes/shape-visual-bounds'
import { CROP_RESIZE_HANDLE_SCREEN_RADIUS } from '@/lib/editor/constants/crop-overlay-constants'
import { CropFrame } from './components/crop-frame'
import { CropMoveBadge } from './components/crop-move-badge'
import { CropPhotoTint } from './components/crop-photo-tint'
import { CropResizeDots } from './components/crop-resize-dots'
import { CropRotateDial } from './components/crop-rotate-dial'
import { CropShapeRotateDot } from './components/crop-shape-rotate-dot'
import {
  computeMoveBadgeLayout,
  computeRotateDialLayout,
  computeShapeRotateDotLayout,
} from './helpers/badge-layout'
import { resolvePhotoTintLayout } from './helpers/photo-tint'
import { useCropCancelDrag } from './hooks/use-crop-cancel-drag'
import { useCropDragMove } from './hooks/use-crop-drag-move'
import { useCropDragStart } from './hooks/use-crop-drag-start'
import { useCropDragState } from './hooks/use-crop-drag-state'
import type { CropOverlayProps, CropOverlayRef } from './types/crop-overlay-props'

/**
 * Crop-mode overlay for a photo-filled shape: a single resizable frame (8 whole-shape handles,
 * not per-node like `NodeEditOverlay`'s pen-path editing) plus a click-and-drag interior that
 * pans the photo within the frame. Frame resize reuses the same photo-fill math as the normal
 * select-tool resize (`computeCropFrameResize`) so corner drags scale the photo with the frame
 * and edge drags crop without rescaling it — see `crop-frame-resize.ts`.
 */
export const CropOverlay = React.forwardRef<CropOverlayRef, CropOverlayProps>(
  (
    {
      shape,
      bleedWidth,
      bleedHeight,
      scale,
      geometryInteractionDisabled = false,
      onFrameChange,
      onFrameCommit,
      onPhotoPanChange,
      onPhotoPanCommit,
      onRotateChange,
      onRotateCommit,
      onShapeRotateChange,
      onShapeRotateCommit,
    },
    ref
  ) => {
    // Effect order: render-phase sync + unmount release (drag state) → imperative handle.
    const state = useCropDragState(shape)
    const { handleResizeDown, handlePanDown, handleRotateDown, handleShapeRotateDown } =
      useCropDragStart({
        state,
        bleedWidth,
        bleedHeight,
        geometryInteractionDisabled,
        shapeRotation: shape.rotation,
      })
    const { handlePointerMove, handlePointerUp } = useCropDragMove({
      state,
      shape,
      bleedWidth,
      bleedHeight,
      onFrameChange,
      onFrameCommit,
      onPhotoPanChange,
      onPhotoPanCommit,
      onRotateChange,
      onRotateCommit,
      onShapeRotateChange,
      onShapeRotateCommit,
    })

    useCropCancelDrag(ref, state)

    if (geometryInteractionDisabled || !shape.photoFill) {
      return null
    }

    const { svgRef, dragRef, geomRef, photoFillRef } = state
    const sw = 1.5 / scale
    const handleHalf = CROP_RESIZE_HANDLE_SCREEN_RADIUS / scale

    const { x: ox, y: oy, width, height } = geomRef.current
    const rotation = shape.rotation ?? 0
    // Rotation always pivots on the GEOMETRIC rect's center — matching ShapeRenderer's own
    // transform-origin and the new visual-bounds architecture's explicit invariant (a shape whose
    // visual box is off-center, e.g. a triangle, does not rotate about its own visual middle).
    const rotCx = ox + width / 2
    const rotCy = oy + height / 2
    const rotTransform = rotation ? `rotate(${rotation}, ${rotCx}, ${rotCy})` : undefined

    // The frame border, resize handles, and center badges must be drawn at the shape's VISUAL
    // rect (the tight box around its actual drawn geometry), not the geometric generator box —
    // matching what normal selection chrome now shows (`selectionChromeFrameOfShape` /
    // `drawableRectOfShape` in `lib/shapes/shape-coordinates.ts`). For a rectangle these are
    // identical; for a star/triangle/custom-path photo container the visual box is the tight
    // shape outline, inset from (and possibly off-center within) the generator box. This only
    // affects DISPLAY — every resize/pan/rotate computation still operates on the geometric
    // rect (`geomRef.current`), which is what's persisted and what the photo's cover-fit frame
    // size (`shape-renderer/helpers/photo-fill-frame.ts`) actually uses.
    const visualRatios = visualInsetRatiosForShape(shape)
    const visualRect = geometricToVisualRect({ x: ox, y: oy, width, height }, visualRatios)

    const tint = resolvePhotoTintLayout(shape, shape.photoFill, width, height)
    const moveBadge = computeMoveBadgeLayout(visualRect, scale)
    // Hidden for the duration of a pan drag (reappears on release) so it doesn't sit on top of the
    // photo while the user is actively repositioning it.
    const isPanning = dragRef.current?.type === 'pan'
    // Hidden for the duration of a rotate drag (both badges — "the icons will disappear"); the
    // rotate badge reappears at the end of its line, which itself rotates with the shape.
    const isRotating = dragRef.current?.type === 'rotate'
    // Reads the live ref (not the shape prop) so the dial follows the in-flight photo rotation.
    const rotateDial = computeRotateDialLayout(
      visualRect,
      moveBadge,
      photoFillRef.current?.rotation ?? 0,
      scale,
      sw,
      handleHalf
    )
    const shapeRotateDot = computeShapeRotateDotLayout(visualRect, scale, sw, handleHalf)

    return (
      <svg
        ref={svgRef}
        className="absolute inset-0 select-none"
        style={{
          width: bleedWidth,
          height: bleedHeight,
          overflow: 'visible',
          zIndex: 55,
          pointerEvents: 'none',
        }}
      >
        <g transform={rotTransform}>
          {/* Full photo + outside-path tint — see `resolvePhotoTintLayout`. MUST render
            before (underneath) the frame border/resize handles below — for a near-rectangular
            shape the mask's hole boundary sits almost exactly where those elements are drawn, so
            painting the tint on top would visibly wash them out rather than just clip a hairline
            sliver (confirmed: an earlier version rendered this last "to avoid shifting other
            tests' rect indices" and it washed out the whole frame border and every handle in
            practice, not just a sliver — reverted). */}
          <CropPhotoTint
            tint={tint}
            ox={ox}
            oy={oy}
            shapeX={shape.x}
            shapeY={shape.y}
            previewUrl={shape.photoFill.previewUrl}
          />

          <CropFrame
            visual={visualRect}
            sw={sw}
            onPanDown={handlePanDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
          />

          <CropResizeDots
            visual={visualRect}
            handleHalf={handleHalf}
            sw={sw}
            rotation={rotation}
            onResizeDown={handleResizeDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
          />

          <CropShapeRotateDot
            layout={shapeRotateDot}
            handleHalf={handleHalf}
            sw={sw}
            onShapeRotateDown={handleShapeRotateDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
          />
        </g>

        {/* Center move badge — decorative only, stays screen-upright (outside the rotation group)
          since it represents screen-space pan directions, not the shape's own orientation.
          Hidden for the duration of a pan OR rotate drag so it doesn't sit on top of the photo
          while the user is actively repositioning/rotating it — reappears when the drag ends. */}
        {!isPanning && !isRotating && <CropMoveBadge layout={moveBadge} sw={sw} />}

        <CropRotateDial
          layout={rotateDial}
          sw={sw}
          isRotating={isRotating}
          isPanning={isPanning}
          onRotateDown={handleRotateDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
        />
      </svg>
    )
  }
)

CropOverlay.displayName = 'CropOverlay'
